/* Progressive enhancements: retain native app selections, sort links and forms. */
(() => {
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const languages = new Map();
  const languageFlags = {disable:'🇩🇪',de:'🇩🇪',nl:'🇳🇱',en:'🇬🇧',fr:'🇫🇷',it:'🇮🇹',es:'🇪🇸',sv:'🇸🇪',pt:'🇵🇹',da:'🇩🇰',pl:'🇵🇱',cs:'🇨🇿',fi:'🇫🇮'};
  const sortButtons = new WeakSet();
  const regionRoots = new WeakSet();
  let activeLanguage = null, sequence = 0, queued = false;

  function closeLanguage(state, restoreFocus = false) {
    if (!state) return;
    clearTimeout(state.hideTimer);
    state.trigger.setAttribute('aria-expanded','false');
    state.menu.classList.remove('is-open');
    state.menu.inert = true;
    state.hideTimer = setTimeout(() => { state.menu.hidden = true; }, reduced() ? 0 : 180);
    if (activeLanguage === state) activeLanguage = null;
    if (restoreFocus && state.trigger.isConnected) state.trigger.focus();
  }
  function openLanguage(state, index) {
    if (activeLanguage && activeLanguage !== state) closeLanguage(activeLanguage);
    clearTimeout(state.hideTimer);
    refreshLanguage(state);
    state.menu.hidden = false; state.menu.inert = false;
    state.trigger.setAttribute('aria-expanded','true');
    activeLanguage = state;
    // Resolve the initial transparent state before starting the fade on the next frame.
    state.menu.getBoundingClientRect();
    requestAnimationFrame(() => {
      if (activeLanguage !== state) return;
      state.menu.classList.add('is-open');
      state.options[Math.max(0,Math.min(index,state.options.length-1))]?.focus();
    });
  }
  function refreshLanguage(state) {
    for (const option of state.options) option.setAttribute('aria-selected',String(option.dataset.value === state.select.value));
    const selected = state.select.selectedOptions[0]?.textContent.trim() || 'Sprache auswählen';
    state.trigger.setAttribute('aria-label','Sprache auswählen: ' + selected);
  }
  function initLanguage(root) {
    if (languages.has(root)) return;
    const select = root.querySelector('select');
    if (!select || select.options.length < 2) return;
    const valid = [...select.options].filter(option => !option.disabled && option.value);
    if (!valid.length) return;
    const controller = new AbortController(), signal = controller.signal;
    const trigger = document.createElement('button');
    trigger.type = 'button'; trigger.className = 'vnk-language-trigger';
    trigger.setAttribute('aria-haspopup','listbox'); trigger.setAttribute('aria-expanded','false');
    const menu = document.createElement('div');
    menu.className = 'vnk-language-menu'; menu.id = 'vnk-language-menu-' + (++sequence);
    menu.setAttribute('role','listbox'); menu.setAttribute('aria-label','Sprache auswählen'); menu.hidden = true; menu.inert = true;
    trigger.setAttribute('aria-controls',menu.id);
    const options = valid.map(option => {
      const button = document.createElement('button'); button.type = 'button'; button.className = 'vnk-language-option';
      button.setAttribute('role','option'); button.dataset.value = option.value; const label = document.createElement('span'); label.className = 'vnk-language-label'; label.textContent = option.textContent.trim().toLocaleUpperCase();
      const flag = document.createElement('span'); flag.className = 'vnk-language-flag'; flag.setAttribute('aria-hidden','true'); flag.textContent = languageFlags[option.value.toLowerCase().split('-')[0]] || '🌐';
      button.append(label,document.createTextNode(' '),flag);
      menu.appendChild(button); return button;
    });
    const state = { root, select, trigger, menu, options, controller, hideTimer:null, previousTabIndex:select.getAttribute('tabindex'), previousAriaHidden:select.getAttribute('aria-hidden') };
    languages.set(root,state);
    root.append(trigger,menu); root.dataset.vnkLanguageReady = '';
    select.tabIndex = -1; select.setAttribute('aria-hidden','true'); refreshLanguage(state);
    trigger.addEventListener('click',event => {
      event.preventDefault(); event.stopPropagation();
      if (trigger.getAttribute('aria-expanded') === 'true') closeLanguage(state);
      else openLanguage(state,Math.max(0,options.findIndex(option => option.dataset.value === select.value)));
    },{signal});
    trigger.addEventListener('keydown',event => {
      if (['ArrowDown','ArrowUp'].includes(event.key)) { event.preventDefault(); openLanguage(state,event.key === 'ArrowUp' ? options.length-1 : 0); }
    },{signal});
    menu.addEventListener('click',event => {
      const option = event.target.closest('.vnk-language-option'); if (!option) return;
      event.preventDefault(); event.stopPropagation();
      closeLanguage(state,true);
      if (select.value !== option.dataset.value) {
        select.value = option.dataset.value;
        select.dispatchEvent(new Event('change',{bubbles:true}));
      }
      refreshLanguage(state);
    },{signal});
    menu.addEventListener('keydown',event => {
      const index = options.indexOf(document.activeElement);
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeLanguage(state,true); return; }
      if (event.key === 'Tab') { closeLanguage(state); return; }
      if (!['ArrowDown','ArrowUp','Home','End'].includes(event.key)) return;
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length-1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
      options[next].focus();
    },{signal});
    select.addEventListener('change',() => refreshLanguage(state),{signal});
  }
  document.addEventListener('pointerdown',event => {
    if (activeLanguage && !activeLanguage.root.contains(event.target)) closeLanguage(activeLanguage);
  });

  /* Place the complete sort list inside the viewport, without an inner scrollbar. */
  function positionSort(button) {
    const menu = document.getElementById(button.getAttribute('aria-controls'));
    if (!menu || button.getAttribute('aria-expanded') !== 'true') return;
    // Mobile sort menus stay anchored below their toggle, even near the viewport edge.
    if (window.matchMedia('(max-width:749.98px)').matches) {
      menu.removeAttribute('data-vnk-sort-position');
      menu.style.removeProperty('--vnk-sort-top');
      menu.style.removeProperty('--vnk-sort-left');
      return;
    }
    const anchor = button.getBoundingClientRect(), height = menu.scrollHeight, width = menu.offsetWidth;
    const edge = 12, view = window.visualViewport;
    const viewportHeight = view?.height || innerHeight, viewportWidth = view?.width || innerWidth;
    const offsetTop = view?.offsetTop || 0, offsetLeft = view?.offsetLeft || 0;
    const below = anchor.bottom + 8, above = anchor.top - height - 8;
    const top = below + height <= offsetTop + viewportHeight - edge ? below : above >= offsetTop + edge ? above : offsetTop + edge;
    const left = Math.max(offsetLeft + edge,Math.min(anchor.right - width,offsetLeft + viewportWidth - width - edge));
    menu.style.setProperty('--vnk-sort-top',top + 'px'); menu.style.setProperty('--vnk-sort-left',left + 'px');
    menu.setAttribute('data-vnk-sort-position','');
  }
  function initSort(button) {
    if (sortButtons.has(button)) return;
    sortButtons.add(button);
    new MutationObserver(() => requestAnimationFrame(() => positionSort(button))).observe(button,{attributes:true,attributeFilter:['aria-expanded']});
    positionSort(button);
  }
  let sortQueued = false;
  function repositionSort() {
    if (sortQueued) return; sortQueued = true;
    requestAnimationFrame(() => {
      sortQueued = false;
      document.querySelectorAll('.collection__nav .popout--sort [aria-controls="sort-list"][aria-expanded="true"]').forEach(positionSort);
    });
  }
  window.addEventListener('resize',repositionSort,{passive:true});
  window.addEventListener('scroll',repositionSort,{passive:true});

  /* Country/currency app lives in an open shadow root; style only its modal. */
  const regionCss = `
    .adt-modal-overlay > div { border-radius:18px !important; background:#fff !important; box-shadow:0 18px 64px #233b2b2b !important; overflow:hidden; }
    .adt-main { background:#fff !important; color:#4b574e !important; padding:28px !important; font-family:'Red Hat Text',sans-serif !important; }
    .adt-main :is(h1,h2,h3) { color:#35483b !important; font-size:21px !important; line-height:1.4 !important; font-weight:500 !important; letter-spacing:0 !important; }
    .adt-main :is(p,label) { font-size:13px !important; line-height:1.65 !important; }
    .adt-main :is(button,select,input) { font-family:'Red Hat Text',sans-serif !important; border-radius:12px !important; font-size:14px !important; font-weight:400 !important; letter-spacing:0 !important; text-transform:none !important; line-height:1.5 !important; transition:background-color .2s ease,border-color .2s ease,box-shadow .2s ease !important; }
    .adt-main button { border:1px solid #cbded1 !important; background:linear-gradient(120deg,#dcebe2,#bed1c9) !important; color:#34523d !important; padding:12px 18px !important; box-shadow:none !important; }
    .adt-main button[role="combobox"], .adt-main button[id^="P0-"], button#P0-0 { width:100% !important; text-align:left !important; background:linear-gradient(120deg,#fff,#f5faf7) !important; border-color:#dce8e1 !important; color:#64766b !important; padding:13px 16px !important; font-family:'Red Hat Text',sans-serif !important; font-size:13px !important; font-weight:400 !important; }
    .adt-main .adt-actions button { background:linear-gradient(120deg,#edf6f0,#d6e7dd) !important; border-color:#c7ded0 !important; color:#546e5f !important; font-family:'Red Hat Text',sans-serif !important; font-size:13px !important; font-weight:500 !important; min-height:46px !important; padding:13px 22px !important; }
    @media(hover:hover) { .adt-main button[role="combobox"]:hover, .adt-main button[id^="P0-"]:hover { background:#f1f7f3 !important; border-color:#bad3c3 !important; } .adt-main .adt-actions button:hover { background:#d9ebdf !important; border-color:#b5cfbe !important; } }
    .adt-main :is(select,input) { border:1px solid #dce6df !important; background:#f7faf8 !important; color:#46594b !important; }
    .adt-main button:focus-visible { outline:2px solid #7b9e88 !important; outline-offset:3px; }
    @media(max-width:749px) {
      .adt-modal-overlay.overlay-bannerModal-type { box-sizing:border-box !important; padding-inline:16px !important; }
      .adt-modal-overlay.overlay-bannerModal-type > div { box-sizing:border-box !important; width:100% !important; max-width:calc(100vw - 32px) !important; margin-inline:auto !important; }
      .adt-main { box-sizing:border-box !important; width:100% !important; padding:22px 32px !important; } .adt-main :is(h1,h2,h3) { font-size:18px !important; } }
  `;
  function initRegion(host) {
    const shadow = host.shadowRoot; if (!shadow || regionRoots.has(shadow)) return;
    regionRoots.add(shadow);
    const apply = () => {
      if (shadow.querySelector('style[data-vnk-region-style]')) return;
      const style = document.createElement('style'); style.dataset.vnkRegionStyle = ''; style.textContent = regionCss; shadow.appendChild(style);
    };
    apply(); new MutationObserver(apply).observe(shadow,{childList:true});
  }

  function init() {
    for (const [root,state] of languages) if (!root.isConnected) {
      closeLanguage(state); clearTimeout(state.hideTimer); state.controller.abort();
      state.trigger.remove(); state.menu.remove(); delete root.dataset.vnkLanguageReady;
      for (const [attribute,value] of [['tabindex',state.previousTabIndex],['aria-hidden',state.previousAriaHidden]]) {
        if (value === null) state.select.removeAttribute(attribute); else state.select.setAttribute(attribute,value);
      }
      languages.delete(root);
    }
    document.querySelectorAll('#ea-auto-translate-root .eat-languageSelector').forEach(initLanguage);
    document.querySelectorAll('.collection__nav .popout--sort [aria-controls="sort-list"]').forEach(initSort);
    document.querySelectorAll('autodetect-root').forEach(initRegion);
  }
  function schedule() { if (!queued) { queued = true; requestAnimationFrame(() => { queued = false; init(); }); } }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',() => { init(); new MutationObserver(schedule).observe(document.body,{childList:true,subtree:true}); },{once:true});
  else { init(); new MutationObserver(schedule).observe(document.body,{childList:true,subtree:true}); }
  document.addEventListener('shopify:section:load',schedule);
  // A shadow root can attach after its host has already been inserted.
  let attempts = 0;
  const regionTimer = setInterval(() => { document.querySelectorAll('autodetect-root').forEach(initRegion); if (++attempts >= 20) clearInterval(regionTimer); },500);
  customElements.whenDefined('autodetect-root').then(() => requestAnimationFrame(() => document.querySelectorAll('autodetect-root').forEach(initRegion)));
})();
